import React, { useEffect, useRef, useState } from 'react';
import { MAX_CART_ITEM_QUANTITY } from '@skin-seoul/service';
import { LucideIcon } from '../../LucideIcon';

const CART_ITEM_QUANTITY_DECREMENT_TRIGGER_TEST_ID = 'cart-item-quantity-decrement-trigger';
const CART_ITEM_QUANTITY_INCREMENT_TRIGGER_TEST_ID = 'cart-item-quantity-increment-trigger';
const CART_ITEM_QUANTITY_INPUT_TEST_ID = 'cart-item-quantity-input';

interface CartNumberInputProps {
  value?: string;
  defaultValue?: string;
  min?: number;
  max?: number;
  step?: number;
  onValueChange?: (details: { value: string; valueAsNumber: number }) => void;
  'data-testid'?: string;
}

const CartNumberInput = ({
  value,
  defaultValue = '1',
  min = 1,
  max = MAX_CART_ITEM_QUANTITY,
  step = 1,
  onValueChange,
  'data-testid': testId,
}: CartNumberInputProps) => {
  const displayValue = value ?? defaultValue;
  const [draft, setDraft] = useState(displayValue);
  const [editing, setEditing] = useState(false);
  const editingRef = useRef(false);
  useEffect(() => setDraft(displayValue), [displayValue]);
  const upperLimit = Math.min(max, MAX_CART_ITEM_QUANTITY);
  const lowerLimit = Math.min(min, upperLimit);
  const numericValue = Number(displayValue) || lowerLimit;
  const inputValue = editing ? draft : displayValue;

  const commitQuantity = (quantity: number) => {
    if (!Number.isSafeInteger(quantity)) return;
    const next = Math.max(lowerLimit, Math.min(upperLimit, quantity));
    if (next !== Number(displayValue)) onValueChange?.({ value: String(next), valueAsNumber: next });
  };

  const handleDecrement = () => {
    commitQuantity(numericValue - step);
  };

  const handleIncrement = () => {
    commitQuantity(numericValue + step);
  };

  return (
    <div
      data-testid={testId}
      className="flex shrink-0 h-8 items-center gap-2.5 border border-neutral-50 bg-neutral-50 rounded-lg px-2"
    >
      <button
        type="button"
        onClick={handleDecrement}
        disabled={editing || numericValue <= lowerLimit}
        aria-label="Decrease quantity"
        data-testid={CART_ITEM_QUANTITY_DECREMENT_TRIGGER_TEST_ID}
        className="flex items-center justify-center w-5 h-5 border-none bg-transparent hover:bg-transparent cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <LucideIcon name="Minus" size={20} className="text-neutral-600" />
      </button>
      <input
        style={{ width: `${Math.min(String(MAX_CART_ITEM_QUANTITY).length, Math.max(1, inputValue.length))}ch` }}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={inputValue}
        onFocus={() => {
          setDraft(displayValue);
          setEditing(true);
          editingRef.current = true;
        }}
        onChange={(event) => {
          setEditing(true);
          editingRef.current = true;
          setDraft(event.target.value);
        }}
        onBlur={() => {
          if (!editingRef.current) return;
          editingRef.current = false;
          setEditing(false);
          if (!/^\d+$/.test(draft) || !Number.isSafeInteger(Number(draft))) return;
          commitQuantity(Number(draft));
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        aria-label="Quantity"
        data-testid={CART_ITEM_QUANTITY_INPUT_TEST_ID}
        className="font-sans h-5 min-w-2 text-center border-0 bg-transparent outline-none p-0 text-label-medium text-neutral-700"
      />
      <button
        type="button"
        onClick={handleIncrement}
        disabled={editing || numericValue >= upperLimit}
        aria-label="Increase quantity"
        data-testid={CART_ITEM_QUANTITY_INCREMENT_TRIGGER_TEST_ID}
        className="flex items-center justify-center w-5 h-5 border-none bg-transparent hover:bg-transparent cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <LucideIcon name="Plus" size={20} className="text-neutral-600" />
      </button>
    </div>
  );
};

export default CartNumberInput;
